import React, { useEffect, useState } from 'react';
import { AuthState } from '../../redux/features/auth/authSlice';
import api from '../../redux/features/auth/axiosAuthHelper';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

export interface OrganisationData {
  id: string;
  name: string;
}

export interface UserData {
  id: number;
  name: string;
}

export interface HousingData {
  id: number;
  name: string;
}

type SearchMethods = "window";

export interface ControlCorrection {
  extension?: number;
  trim?: number;
  div_profile?: number;
  step_search?: number;
  search_min?: number;
  search_max?: number;
  min_fit_percentage?: number;
  search_method?: SearchMethods;
}

export interface StripRegion {
  category: string;
  lower_bound: number;
  upper_bound: number;
}

export interface Strip {
  position: number;
  label: string;
  sample_rows: number;
  sample_cols: number;
  image_corners: [number, number][];
  profile: number[];
  strip_regions: StripRegion[];
  control_correction?: ControlCorrection;
}

export interface ImageItem {
  result_id: string;
  image_url: string;
  homography: number[];
  timestamp: string;
}

export interface Method {
  id?: number | string;
  housing: number | string;
  test_name: string;
  description: string;
  version?: number;
  status?: string;
  flash_required: boolean;
  collection_mode: boolean;
  initial_h_factor?: number;
  control_line_correction?: boolean;
  profile_alignment?: boolean;
  control_exposure?: boolean;
  profile_control_levels?: number[]; // optional
  polynomial_baseline_order?: number;
  frames_to_capture: number;
  control_baseline?: boolean;
  control_baseline_threshold?: number; // optional
  strip_image: ImageItem | null;
  strips: Strip[];
}

export const interpretationColours = {
  POSITIVE: "#C62828",   // A strong, professional green
  NEGATIVE: "#2E7D32",   // A clear, assertive red
  BORDERLINE: "#F9A825", // A balanced amber, indicating caution
  INVALID: "#9E9E9E",    // A neutral grey for invalid results
}

export type InterpretationOption = keyof typeof interpretationColours; // "POSITIVE" | "NEGATIVE" | "BORDERLINE" | "INVALID"
export const ALL_INTERPRETATIONS = Object.keys(interpretationColours) as InterpretationOption[];

export const interpretationLabels = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  BORDERLINE: 'BORDERLINE', // change this value
  INVALID: 'INVALID'
};

export interface Result {
  id: string;
  app_interpretation: InterpretationEnum;
  final_result_interpretation: InterpretationEnum;
  visual_result: VisualResult | null;
  type: string;
  aborted_reason: string | null;
  test_type: string;
  batch_id: number;
  lot_number: string;
  sample_id: string;
  sample_type: string;
  timestamp: string; // ISO date string
  longitude: number;
  latitude: number;
  device_model: string;
  device_platform: string;
  device_manufacturer: string;
  device_uuid: string;
  os_version: string;
  app_version: string;
  app_build: number;
  test_strips: TestStrip[];
  notes: string;
  custom_fields: Record<string, string>;
  full_image_path: string;
  strip_image_paths: string[];
  organisation_id: number;
  organisation_name: string;
  user_id: number;
}

export interface VisualResult {
  visual_interpretation?: InterpretationEnum | null;
  is_reviewed: boolean;
  notes?: string | null;
  reviewer_id?: number | null;
  reviewer_interpretation?: InterpretationEnum | null;
  reviewer_organisation_id?: number | null;
  reviewed_timestamp?: string | null; // ISO date string
  reviewer_notes?: string | null;
}

export type InterpretationEnum = "POSITIVE" | "NEGATIVE" | "INVALID" | "BORDERLINE";
// agrega aquí todos los valores que definas en tu Enum en backend

export interface TestStrip {
  lines: Line[];
  c_line: CLine;
  baseline: number[];
  profile: number[];
  strip_image_base64: string | null;
  c_color?: string;
  profile_color?: string;
  baseline_color?: string;
  main_image?: string;
  mean_profile?: number[];
}

export interface Line {
  score?: number;
  tc_ratio?: number;
  peak_position?: number;
  t_color: string | null;
  area?: number | null,
}

export interface CLine {
  score: number | null;
  peak_position: number | null;
  area: number | null;
}

export interface ReviewState {
  [resultId: string]: {
    interpretation: InterpretationEnum;
    notes: string;
  };
}

export interface ChangeActionSelected {
  [resultId: string]: boolean;
}

export const capitalizeFirstLetter = (word: string): string => {
  const lower = word.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Custom hook to fetch and manage clinic data based on the user type.
 * @param authData - The authData object containing authentication-related information.
 * @returns An array containing clinics.
 */
const useOrganisations = () => {
  // State to hold the fetched clinic data.
  const [organisations, setOrganisations] = useState<OrganisationData[]>([]);
  const authData = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Skip fetching if the user is not authenticated.
    if (!authData.isAuthenticated) {
      return;
    }

    // Asynchronous function for fetching clinic data.
    const fetchData = async () => {
      try {
        let organisationData: OrganisationData[] = [];
        if (authData.isSuperUser || authData.isDDTDAdmin) {
          // Fetch Organisations using the 'api/organisations/' endpoint for administrators and support users.
          const response = await api.get('organisations/', {
            headers: {
              Authorization: `Bearer ${authData.access}`,
            },
          });

          organisationData = (response.data.results as OrganisationData[]).map(
            ({ id, name }) => ({
              id,
              name,
            }),
          );
          // Update the component state with the fetched organisation data.
          setOrganisations(organisationData);
        } else if (authData.isOrganisationAdmin || authData.isOrganisationUser) {
          organisationData = [
            {
              id: String(authData.user?.organisation),
              name: authData.user?.organisation_name || '',
            },
          ];

          // Update the component state with the fetched organisation data.
          setOrganisations(organisationData);
        }
        console.log('-----Organisations------');
        console.log(organisationData);
      } catch (error) {
        // Log an error message if there is an issue fetching organisation data.
        console.error('Error fetching organisation data:', error);
      }
    };

    // Call the fetchData function to initiate the effect.
    fetchData();
  }, [authData.isAuthenticated, authData.isSuperUser, authData.isDDTDAdmin, authData.isOrganisationUser, authData.isOrganisationAdmin]);

  return [organisations] as const;
};

const useRareOrganisations = (): OrganisationData[] => {
  // State to hold the fetched clinic data.
  const [organisations, setOrganisations] = useState<OrganisationData[]>([]);
  const authData = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Skip fetching if the user is not authenticated.
    if (!authData.isAuthenticated) {
      return;
    }

    // Asynchronous function for fetching clinic data.
    const fetchData = async () => {
      try {
        let organisationData: OrganisationData[] = [];
        let downloadPageSize = 250;  // Set the batch size to 250
        let downloadOffset = 0;
        let total = 0;
        if (authData.isSuperUser || authData.isDDTDAdmin) {
          // Fetch Organisations using the 'api/organisations/' endpoint for administrators and support users.
          const initialResponse = await api.get(`organisations/?limit=${downloadPageSize}&offset=${downloadOffset}`, {
            headers: {
              Authorization: `Bearer ${authData.access}`,
            },
          });

          organisationData = (initialResponse.data.results as OrganisationData[]).map(
            ({ id, name }) => ({
              id,
              name,
            }),
          );
          // Update the component state with the fetched organisation data.
          total = initialResponse.data.count;
          setOrganisations([...organisationData].sort((a, b) => Number(b.id) - Number(a.id)));


          // Now fetch the remaining in the background
          while (organisationData.length < total) {
            downloadOffset += downloadPageSize;
            const url = `organisations/?limit=${downloadPageSize}&offset=${downloadOffset}`

            const response = await api.get(url, {
              headers: {
                'Authorization': `Bearer ${authData.access}`
              }
            });
            const nextPageorganisationData = (response.data.results as OrganisationData[]).map(
              ({ id, name }) => ({
                id,
                name,
              }),
            );

            organisationData.push(...nextPageorganisationData);
            console.log(`Downloaded ${organisationData.length} of ${total} organisations`);
            setOrganisations([...organisationData].sort((a, b) => Number(b.id) - Number(a.id)));
          }

        } else if (authData.isOrganisationAdmin || authData.isOrganisationUser) {
          organisationData = [
            {
              id: String(authData.user?.organisation),
              name: authData.user?.organisation_name || '',
            },
          ];

          // Update the component state with the fetched organisation data.
          setOrganisations([...organisationData]);
        }
        console.log('-----Organisations------');
        console.log(organisationData);
      } catch (error) {
        // Log an error message if there is an issue fetching organisation data.
        console.error('Error fetching organisation data:', error);
      }
    };

    // Call the fetchData function to initiate the effect.
    fetchData();
  }, [authData]);

  return organisations;
};


const useRareUsers = (): UserData[] => {
  const [users, setUsers] = useState<UserData[]>([]);
  const authData = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!authData.isAuthenticated) {
      return;
    }

    const fetchData = async () => {
      try {
        let userData: UserData[] = [];
        const downloadPageSize = 250;
        let downloadOffset = 0;
        let total = 0;

        if (authData.isSuperUser || authData.isDDTDAdmin) {
          const initialResponse = await api.get(
            `users/?limit=${downloadPageSize}&offset=${downloadOffset}`,
            { headers: { Authorization: `Bearer ${authData.access}` } }
          );

          userData = (initialResponse.data.results as any[]).map((user) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim(),
          }));
          total = initialResponse.data.count;
          setUsers([...userData]);

          while (userData.length < total) {
            downloadOffset += downloadPageSize;
            const response = await api.get(
              `users/?limit=${downloadPageSize}&offset=${downloadOffset}`,
              { headers: { Authorization: `Bearer ${authData.access}` } }
            );
            const nextPageUserData = (response.data.results as any[]).map((user) => ({
              id: user.id,
              name: `${user.first_name} ${user.last_name}`.trim(),
            }));
            userData.push(...nextPageUserData);
            setUsers([...userData]);
          }
        } else {
          userData = [
            {
              id: authData.user?.id as number,
              name: `${authData.user?.first_name} ${authData.user?.last_name}`.trim(),
            },
          ];
          setUsers(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData();
  }, [authData]);

  return users;
};

export {
  useOrganisations,
  useRareOrganisations,
  useRareUsers
};
